import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight, Shield, Zap, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';

interface CTASectionProps {
  onScrollToDemo: () => void;
}

export const CTASection = ({ onScrollToDemo }: CTASectionProps) => {
  const guarantees = [
    { icon: Clock, text: "Trials available on request" },
    { icon: Shield, text: "GDPR compliant" },
    { icon: Zap, text: "5-minute setup" },
  ];

  return (
    <section className="w-full py-20 md:py-32 bg-gradient-to-br from-primary via-primary to-primary/90 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-full h-full" 
          style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, white 1px, transparent 1px),
                              radial-gradient(circle at 80% 50%, white 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}
        />
      </div>

      <div className="container relative z-10 px-4 md:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">
              Your AI revenue workforce<br />starts here.
            </h2>
            <p className="text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto">
              Starts by assisting your team. Evolves to running your entire revenue operations autonomously. No headcount required.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <Button
                size="lg"
                className="bg-white text-primary hover:bg-white/90 font-bold px-12 py-7 text-xl rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 group"
                onClick={onScrollToDemo}
              >
                Talk to Sales
                <ArrowRight className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                size="lg"
                className="bg-white/20 border-2 border-white text-white hover:bg-white hover:text-primary font-bold px-8 py-7 text-lg rounded-2xl backdrop-blur-sm transition-all duration-300"
                onClick={onScrollToDemo}
              >
                Contact Us
              </Button>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-6 text-white/80">
              {guarantees.map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <item.icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            className="mt-12 pt-8 border-t border-white/10"
          >
            <p className="text-white/60 text-sm">
              🇪🇺 EU-based · GDPR Compliant · Your data never leaves Europe
            </p>
            <Link to="/crm-agent" className="inline-flex items-center gap-1 text-white/80 hover:text-white text-sm mt-3 underline underline-offset-2">
              Learn about our CRM AI Agent <ArrowRight className="h-3 w-3" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
